/**
 * The lock on a learner's reading place.
 *
 * A place is written to the learner's own repo, and a repo is a public thing: the
 * record sits at a key anybody can list, and the bytes at it are readable by
 * anybody who asks. What is in there is nobody's business — which book, which
 * page, how long they have been on it, and the answers they have given on the way.
 *
 * So the place travels *sealed*. The key is not stored anywhere it could be read
 * off the wire: it is worked out from the one thing only the learner has, the
 * password they sign in with, together with the account it belongs to. The app
 * derives it while the password is in hand at sign-in, keeps the derived key on
 * the device, and lets the password go exactly as it always has (see
 * `#/lib/flashbookVaultStore`).
 *
 * Four decisions shape this file.
 *
 *  - **The account's own DID is the salt.** A salt exists so two learners who
 *    chose the same password do not get the same key, and a *random* one does that
 *    marginally better — at a price this feature cannot pay. A random salt has to
 *    travel with the record for the second device to work out the same key, and two
 *    devices that each wrote a record before reading one would have sealed them
 *    under different keys and could never open each other's. A DID is unique per
 *    learner, public already — it is in the repo's own address — and identical on
 *    every device, so both halves of the trade come out right: the key agrees
 *    everywhere, and a guess still costs one PBKDF2 run *per learner* rather than
 *    one for the app.
 *
 *  - **PBKDF2-HMAC-SHA256, at a work factor chosen for a client.** The cost is paid
 *    once, at sign-in, on a path that has just waited for a network round trip, and
 *    it is paid in pure JavaScript on every platform the app runs on. A hundred
 *    thousand iterations is a fraction of a second on a laptop and under a second
 *    on a phone; the several hundred thousand that server-side guidance suggests
 *    would freeze a phone for seconds to protect a record that is public in every
 *    other respect.
 *
 *  - **AES-GCM, because it is *authenticated*.** A record this device cannot open is
 *    not a read it loses, it is a read it must refuse: a ciphertext somebody edited
 *    has to end up in the same place as one that is damaged, and the device's own
 *    copy already wins every disagreement (see `mergeRemoteRead`). GCM will not open
 *    a touched ciphertext, so a tampered place is no place at all rather than a
 *    resume onto a page the learner never wrote.
 *
 *  - **Nothing here throws.** Every failure — a password too short to be worth a
 *    key, a platform with no random source, a ciphertext that does not open — ends
 *    as `null`, because every caller already has a defined behaviour for "no key"
 *    and for "nothing to read". And `null` from a *seal* is load-bearing: it is what
 *    makes "a place that cannot be sealed is not written at all" true by
 *    construction rather than by every caller remembering it (see
 *    `progressRecordFor`).
 */
import {gcm} from '@noble/ciphers/aes.js'
import {pbkdf2} from '@noble/hashes/pbkdf2.js'
import {sha256} from '@noble/hashes/sha2.js'
import {randomBytes, utf8ToBytes} from '@noble/hashes/utils.js'

/**
 * The iterations a key is worked out with.
 *
 * Exported because the number is part of the wire format rather than a private
 * tuning knob: change it and every record sealed before the change stops opening,
 * so a unit test freezes a whole derived key to make that visible before it ships.
 */
export const FLASHBOOK_VAULT_ITERATIONS = 100_000

/**
 * How long a key is, in bytes. AES-256.
 *
 * Spelled out in the type of the place it is checked — {@link sealFlashBookPayload}
 * refuses a key that is not this long rather than handing a short one to the
 * cipher, so "somehow a two-byte key" fails closed.
 */
const KEY_BYTES = 32

/** The IV length AES-GCM is defined for. Not a choice: GCM is standardised on 96 bits. */
const IV_BYTES = 12

/** The salt is a hash, so its length is a choice. 128 bits is the usual one. */
const SALT_BYTES = 16

/**
 * The payload of a sealed value, as it is stored.
 *
 * Two base64 strings and nothing else, so the whole thing survives a round trip
 * through a JSON record (a `Uint8Array` does not) without depending on any platform
 * having `Buffer`. The tag lives at the end of `ct`, which is where AES-GCM puts it.
 */
export type FlashBookSeal = {
  /** The initialisation vector this seal was made with, base64. */
  iv: string
  /** The sealed bytes with their authentication tag, base64. */
  ct: string
}

/**
 * The salt a learner's key is derived under.
 *
 * A hash of the account rather than of the record, so it can be computed *before*
 * anything is fetched — which is the property the whole scheme rests on, because a
 * device that has never seen a record still has to be able to write one the other
 * device can open. The version string is mixed in so a later change of derivation
 * can be a new generation of keys rather than a silent re-interpretation of the
 * old ones.
 */
export function flashbookVaultSalt(did: string): Uint8Array {
  // The label is hashed as *bytes*: the hash takes a byte array rather than a string
  // on every platform, and a salt that only worked where a library happened to accept
  // text would be a salt that silently became no salt at all.
  const label = utf8ToBytes(`app.bsky.feed.flashbookProgress/v1|${did}`)
  return sha256(label).slice(0, SALT_BYTES)
}

/**
 * The key a learner's places are sealed under, from the password they signed in
 * with — or `null` when there is nothing to derive one from.
 *
 * Pure and synchronous, deliberately: it is one computation at one moment, and
 * every caller — the sign-in path, the fixtures, the tests — can have the key in
 * hand the instant they ask for it. The password is not kept: it is a parameter to
 * this function and nothing else.
 */
export function deriveFlashBookVaultKey({
  password,
  did,
}: {
  password: string
  /** The account the key is for — its DID, which is also the salt. */
  did: string
}): Uint8Array | null {
  if (!password || !did) return null
  try {
    return pbkdf2(sha256, password, flashbookVaultSalt(did), {
      c: FLASHBOOK_VAULT_ITERATIONS,
      dkLen: KEY_BYTES,
    })
  } catch {
    return null
  }
}

/**
 * Seal a value, or `null` if this device cannot.
 *
 * The IV is fresh per seal — the one thing AES-GCM must never reuse under a key —
 * so `null` here also covers a platform with no random source, which is the honest
 * answer: a place that cannot be sealed is not written in the clear instead.
 */
export function sealFlashBookPayload(
  key: Uint8Array | null | undefined,
  value: unknown,
): FlashBookSeal | null {
  if (!key || key.length !== KEY_BYTES) return null
  try {
    const iv = randomBytes(IV_BYTES)
    const sealed = gcm(key, iv).encrypt(utf8ToBytes(JSON.stringify(value)))
    return {iv: toBase64(iv), ct: toBase64(sealed)}
  } catch {
    return null
  }
}

/**
 * Open a sealed value, or `null` when it will not open.
 *
 * A wrong key, a missing key, a truncated blob and an edited one all end the same
 * way, because the caller's question is only ever "is there a place here for *this*
 * learner" — and the answer to all four is no.
 */
export function openFlashBookPayload(
  key: Uint8Array | null | undefined,
  seal: FlashBookSeal,
): unknown | null {
  if (!key || key.length !== KEY_BYTES) return null
  const iv = fromBase64(seal.iv)
  const ct = fromBase64(seal.ct)
  // A ciphertext shorter than its own tag cannot be one, and is refused before the
  // cipher sees it: the length is a property of the encoding, not of the key.
  if (!iv || !ct || iv.length !== IV_BYTES || ct.length <= 16) return null
  try {
    const opened = new TextDecoder().decode(gcm(key, iv).decrypt(ct))
    return JSON.parse(opened)
  } catch {
    return null
  }
}

/** A key as it is kept on the device: bytes have nowhere to live in a JSON store. */
export function vaultKeyToBase64(key: Uint8Array): string {
  return toBase64(key)
}

/** A key read back, or `null` when what was stored is not a key of the right shape. */
export function vaultKeyFromBase64(
  value: string | undefined,
): Uint8Array | null {
  if (!value) return null
  const key = fromBase64(value)
  return key && key.length === KEY_BYTES ? key : null
}

/** The base64 alphabet, in the order RFC 4648 counts in. */
const ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

/**
 * Bytes as base64, by hand.
 *
 * `btoa` is not on every platform this app runs on and `Buffer` is on none of the
 * ones that matter; seventeen lines that behave the same everywhere are cheaper
 * than that question, and the two directions are pinned against each other by a
 * unit test rather than by trusting a host to agree with itself.
 */
function toBase64(bytes: Uint8Array): string {
  let out = ''
  for (let index = 0; index < bytes.length; index += 3) {
    const a = bytes[index]
    const b = bytes[index + 1]
    const c = bytes[index + 2]
    const triple = (a << 16) | ((b ?? 0) << 8) | (c ?? 0)
    out += ALPHABET[(triple >> 18) & 63]
    out += ALPHABET[(triple >> 12) & 63]
    out += b === undefined ? '=' : ALPHABET[(triple >> 6) & 63]
    out += c === undefined ? '=' : ALPHABET[triple & 63]
  }
  return out
}

/**
 * Base64 as bytes, or `null` when the text is not base64.
 *
 * Strict on purpose. Padding is not tolerated in the middle, the alphabet is the
 * only one accepted, and a length that cannot have come from three-byte groups is
 * refused — a decoder that guessed would turn a corrupted record into a *different*
 * key and a place that opens onto nonsense.
 */
function fromBase64(text: string): Uint8Array | null {
  if (!text || text.length % 4 !== 0) return null
  const bytes: number[] = []
  let triple = 0
  let count = 0
  for (const char of text) {
    if (char === '=') break
    const value = ALPHABET.indexOf(char)
    if (value < 0) return null
    triple = (triple << 6) | value
    count += 1
    if (count === 4) {
      bytes.push((triple >> 16) & 255, (triple >> 8) & 255, triple & 255)
      triple = 0
      count = 0
    }
  }
  if (count === 1) return null
  if (count === 2) bytes.push((triple >> 4) & 255)
  if (count === 3) {
    bytes.push((triple >> 10) & 255, (triple >> 2) & 255)
  }
  return Uint8Array.from(bytes)
}
